import { _getLogger } from '@doxyme/analytics';
import { attachHandler, Specs } from '@doxyme/extensions-common';
import type ExtensionInstance from '../../../instance';
import ControlCapability from '../control';

const { SidebarMenuControl } = Specs;
type Constraints = Specs.SidebarMenuControl.Constraints;

export type ResolveFeatureFlagHandler = (feature: string) => Promise<unknown>;

const logger = _getLogger('sidebarMenuControl', {
  component: 'extensions',
  lib: 'bridge',
});

export default class SidebarMenuControlCapability extends ControlCapability<Constraints> {
  static id = SidebarMenuControl.id;

  // In practice only one handler is registered (the host's GrowthBook
  // resolver). It is kept as an array to mirror `FeatureFlagCapability.onGet`
  // and reuse the same `attachHandler` detach mechanism.
  protected readonly resolveFeatureFlagHandlers: ResolveFeatureFlagHandler[] =
    [];

  constructor() {
    super(SidebarMenuControl);
  }

  /**
   * Attach a host-registered handler that resolves a feature flag value for
   * the current user (mirrors `FeatureFlagCapability.onGet`). Used to decide
   * whether a control configured with `featureFlag` should be shown.
   * @param handler - The handler to attach
   * @returns A function to detach the handler
   */
  onResolveFeatureFlag(handler: ResolveFeatureFlagHandler): () => void {
    return attachHandler(this.resolveFeatureFlagHandlers, handler);
  }

  /**
   * @hidden
   */
  override addInstance(instance: ExtensionInstance): void {
    // Only one control per extension is supported, we take first one.
    const config = this.configsBy(instance)[0];
    const featureFlag = config?.featureFlag;

    if (!config || !featureFlag) {
      super.addInstance(instance);
      return;
    }

    this.addInstanceBase(instance);
    void this.#addControlIfFeatureFlagEnabled(instance, config, featureFlag);
  }

  /**
   * Resolves the feature flag and publishes the control only when the flag
   * resolves truthy. Fails closed: a resolver error or a missing resolver
   * keeps the control hidden.
   */
  async #addControlIfFeatureFlagEnabled(
    instance: ExtensionInstance,
    config: Constraints['config'],
    featureFlag: string,
  ): Promise<void> {
    let value: unknown;
    try {
      for (const handler of this.resolveFeatureFlagHandlers) {
        value = await handler(featureFlag);
      }
    } catch (error) {
      logger.warn(
        `Failed to resolve feature flag for sidebar-menu-control: ${featureFlag}`,
        {
          featureFlag,
          error,
          manifest: {
            id: instance.manifest.id,
            name: instance.manifest.name,
          },
        },
      );
      return;
    }

    if (!value) {
      return;
    }

    // The instance may have been removed while the flag was resolving.
    if (!this.hasInstance(instance.id)) {
      return;
    }

    this.addControl(instance, config);
  }
}
